/**
 * Organization-manager (role 5) list.
 *
 * The backend scopes the rows to the organizations the caller owns/manages
 * (admin sees all) and returns an `organizations_array` of the manager's
 * organization names.
 */

import { useMemo, useState } from 'react';
import {
  List,
  TextField,
  EmailField,
  FunctionField,
  useTranslate,
} from 'react-admin';
import { CustomDatagrid, CrudListActions, RowActionsMenu } from '@/components/common';
import { compactListSx } from '@/components/common/compactListSx';
import {
  CollapsibleFilterPanel,
  FilterExpandContext,
} from '@/components/common/CollapsibleFilterPanel';
import { CustomTextInputFilter } from '@/components/common/CustomFilters';

export const OrgManagerList = () => {
  const translate = useTranslate();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterExpandValue = useMemo(
    () => ({ expanded: filtersExpanded, setExpanded: setFiltersExpanded }),
    [filtersExpanded]
  );

  const filters = useMemo(
    () => [
      <CollapsibleFilterPanel key="filters" source="_cfp" alwaysOn alwaysVisibleCount={0}>
        <CustomTextInputFilter
          source="q"
          label={translate('common.filters.search', { _: 'Buscar' })}
          alwaysOn
        />
      </CollapsibleFilterPanel>,
    ],
    [translate]
  );

  return (
    <FilterExpandContext.Provider value={filterExpandValue}>
      <List
        sx={compactListSx}
        filters={filters}
        actions={<CrudListActions exportColumns={['full_name', 'email', 'contact_no', 'status']} />}
        sort={{ field: 'user_id', order: 'DESC' }}
        exporter={false}
      >
        <CustomDatagrid primarySource="full_name" secondarySource="email" bulkActionButtons={false}>
          <RowActionsMenu label="" sortable={false} />
          <TextField source="full_name" />
          <EmailField source="email" />
          <FunctionField
            source="organizations_array"
            render={(record: any) =>
              Array.isArray(record?.organizations_array)
                ? record.organizations_array.join(', ')
                : ''
            }
            sortable={false}
          />
          <FunctionField
            source="status"
            render={(record: any) =>
              record?.status === 1
                ? translate('common.status.active', { _: 'Activo' })
                : translate('common.status.inactive', { _: 'Inactivo' })
            }
          />
        </CustomDatagrid>
      </List>
    </FilterExpandContext.Provider>
  );
};

export default OrgManagerList;
